import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Divider, FormHelperText, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { PlatformPeopleService } from '../../../../../Component/Api/PlatformPeople/PlatformPeopleService';
import { CoreStaticDataService } from '../../../../../Component/Api/StaticData/CoreStaticDataService';
import AppCheckbox from '../../../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { IntermediateGlobalLoading } from '../../../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { AllOccupationalLevelList, EmployeeLevelList, genderList, provinceList, raceList } from '../../../../../Component/Common/Constants/Lists/DropdownListOptions';
import { CreatePlatformPeopleRequest, PlatformPeople, PlatformPeopleEmploymentData, ValidateUniquePerson } from '../../../../../Component/Model/Dto/Platform/PlatformPeople';
import { OccupationalLevel } from '../../../../../Component/Model/Enum/OccupationalLevel';
import { ProvinceEnum } from '../../../../../Component/Model/Enum/ProvinceEnums';
import { getIOptionsArray } from '../../../../../Helpers/DropdownWithDescriptionHelper';
import { OrganisationAtom } from '../../../../../State/Organisation.Atom';
import { CreateOrganisationPersonValidationSchema } from './CreateOrganisationPersonValidationSchema';
import { BreadCrumb } from '../../../../../Component/Common/Components/BreadCrumb/BreadCrumb';
import { getDateWithoutTime } from '../../../../../Helpers/DateFormatter';

export const CreateOrganisationPerson = () => {
    const history = useHistory();
    const [municipalityOptions, setMunicipalityOptions] = React.useState<IDropDownOptions[]>([]);
    const [nationalityOptions, setNationalityOptions] = React.useState<IDropDownOptions[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const { control, watch, handleSubmit, formState: { isSubmitting }, reset, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(CreateOrganisationPersonValidationSchema),
    });
    const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
    const [showIdNumberError, setShowIdNumberError] = useState<boolean>(false);
    const [showEmployeeCodeError, setShowEmployeeCodeError] = useState<boolean>(false);
    const [idNumber, setIdNumber] = useState<string>("");
    const [employeeCode, setEmployeeCode] = useState<string>("");

    const endDateText = 'Required only if the employee has been since termindated';
    const municipalityText = 'This is the municipality for which the employee is currently positioned. This could be a requirement for your WSP/ATR Submission';
    const siteNameText = 'This is the location for which the employee is currently positioned';
    const provinceText = 'This is the province for which the employee is currently positioned';

    const fetchMuniciplaityData = async () => {
        try {
            const result = await CoreStaticDataService.getMunicipalities();
            if (result.data) {
                setMunicipalityOptions(getIOptionsArray(result.data, "id", "name"));
            } else {
                toast.warn("No Municipalities found")
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchNationalityData = async () => {
        try {
            const result = await CoreStaticDataService.getNationalities();
            if (result.data) {
                setNationalityOptions(getIOptionsArray(result.data, "id", "name"));
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchData = async () => {
        setLoading(true);
        const municipalityPromise = fetchMuniciplaityData();
        const nationalityPromise = fetchNationalityData();
        await Promise.all([municipalityPromise, nationalityPromise]);
        setLoading(false);
    }


    const validateIdNumber = async () => {
        setShowIdNumberError(false);
        setShowEmployeeCodeError(false);

        if (idNumber != null && idNumber !== "") {
            let request: ValidateUniquePerson = {
                value: idNumber,
                organisationId: peopleAndTrainingOrganisation.OrganisationId
            };

            let idNumberExist = await PlatformPeopleService.idNumberExist(request);
            setShowIdNumberError(idNumberExist.data);
        }

        if (idNumber == "") {
            validateEmployeeCode();
        }
    }

    const validateEmployeeCode = async () => {
        setShowEmployeeCodeError(false);

        if (employeeCode != null && (idNumber == null || idNumber === "")) {
            let request: ValidateUniquePerson = {
                value: employeeCode,
                organisationId: peopleAndTrainingOrganisation.OrganisationId
            };

            let employeeCodeExist = await PlatformPeopleService.employeeCodeExist(request);
            setShowEmployeeCodeError(employeeCodeExist.data);
        }
    }

    const idNumberChanged = async (data: FieldValues) => {
        setIdNumber(data.currentTarget.value);
    }

    const employeeCodeChanged = async (data: FieldValues) => {
        setEmployeeCode(data.currentTarget.value);
    }

    useEffect(() => {
        fetchData();
    }, [])

    const handleDataSubmit = async (data: FieldValues) => {

        let personalInfo: PlatformPeople = {
            organisationId: peopleAndTrainingOrganisation.OrganisationId,
            firstName: data.personalInfo.firstName,
            lastName: data.personalInfo.lastName,
            idNumber: data.personalInfo.idNumber,
            employeeCode: data.personalInfo.employeeCode,
            raceId: data.personalInfo.race,
            genderId: data.personalInfo.gender,
            dateOfBirth: data.personalInfo.dateOfBirth,
            nationalityId: data.personalInfo.nationality == null || data.personalInfo.nationality === 0 ? null : data.personalInfo.nationality,
            disabled: data.personalInfo.isDisabled
        }
        let employmentData: PlatformPeopleEmploymentData = {
            startDate: data.employmentData.startDate,
            endDate: data.employmentData.endDate,
            jobTitle: data.employmentData.job,
            occupationalLevelId: data.employmentData.occupation == null || data.employmentData.occupation === 0 ? OccupationalLevel.Unspecified : data.employmentData.occupation,
            employmentTypeId: data.employmentData.employmentType,
            municipalityId: data.employmentData.municipality == null || data.employmentData.municipality === 0 ? null : data.employmentData.municipality,
            siteName: data.employmentData.siteName,
            provinceId: data.employmentData.province == null || data.employmentData.province === 0 ? ProvinceEnum.Unspecified : data.employmentData.province
        }

        const createPlatformPerson: CreatePlatformPeopleRequest = {
            personalInfo,
            employmentData
        }

        try {
            if (!showIdNumberError && !showEmployeeCodeError) {
                const result = await PlatformPeopleService.createPlatformPerson(createPlatformPerson);
                if (result.data) {
                    toast.success(`Person successfuly created`)
                    history.push(`/organisation/people/person/edit/${result.data}`)
                }
            }
        } catch (error: any) {
            console.log(error);
            toast.error(error.response.data);
        }
    }

    if (loading) return (
        <IntermediateGlobalLoading />
    )
    return (
        <>
            <BreadCrumb
                titles={['Manage People', 'Person']}
                hrefs={[`/organisation/people`, '']}
                currentLinkIndex={1}
            />
            
            <Typography variant="h4" style={{ paddingBottom: "20px" }}>
                People
            </Typography>
            <Typography variant="body2" style={{ paddingBottom: "20px" }}>
                Capture personal information and the most recent employment history. You will be able to capture more employment history records once saved.
            </Typography>
            <Grid component="form" id='personForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Typography>
                    Personal Information
                </Typography>
                <Grid item xs={12} container spacing={3} >
                    <Grid item xs={6}>
                        <Box mt={2}>
                            <AppTextInput control={control} name="personalInfo.firstName" label="First name *" />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="personalInfo.idNumber" label="ID number or Passport" showError={showIdNumberError} onBlur={validateIdNumber} onChange={idNumberChanged} />
                            {showIdNumberError &&
                                <FormHelperText sx={{ color: 'red' }}>ID number already exists in this organisation</FormHelperText>
                            }
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="personalInfo.race" label="Race *" items={raceList} />
                        </Box>
                        <Box mt={2}>
                            <AppDatepicker control={control} name="personalInfo.dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' maxDate={getDateWithoutTime(new Date())} />
                        </Box>
                        <Box mt={2}>
                            <AppCheckbox control={control} name='personalInfo.isDisabled' label='Disabled' disabled={false} />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box mt={2}>
                            <AppTextInput control={control} name="personalInfo.lastName" label="Surname *" />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="personalInfo.employeeCode" label="Employee Code" showError={showEmployeeCodeError} onBlur={validateEmployeeCode} onChange={employeeCodeChanged} />
                            {showEmployeeCodeError &&
                                <FormHelperText sx={{ color: 'red' }}>Employee Code already exists in this organisation</FormHelperText>
                            }
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="personalInfo.gender" label="Gender *" items={genderList} />
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="personalInfo.nationality" label="Nationality" items={nationalityOptions} />
                        </Box>
                    </Grid>
                </Grid>
                <Divider variant="middle" />
                <Typography>
                    Employment History
                </Typography>
                <Grid item xs={12} container spacing={3}>
                    <Grid item xs={6}>
                        <Box mt={2}>
                            <AppDatepicker control={control} name='employmentData.startDate' label='Start Date *' inputFormat='yyyy/MM/dd' />
                        </Box>

                        <Box mt={2}>
                            <AppTextInput control={control} name="employmentData.job" label="Job Title *" />
                        </Box>

                        <Box mt={2}>
                            <AppSelectList control={control} name="employmentData.employmentType" label="Employment Type *" items={EmployeeLevelList} />
                        </Box>

                        <Box mt={2}>
                            <AppTextInput control={control} name="employmentData.siteName" label="Site Name" showDescription descriptionText={siteNameText} />
                        </Box>

                    </Grid>
                    <Grid item xs={6}>
                        <Box mt={2}>
                            <AppDatepicker control={control} name='employmentData.endDate' label='End Date' inputFormat='yyyy/MM/dd' showDescription descriptionText={endDateText} />
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="employmentData.occupation" label="Occupational Level" items={AllOccupationalLevelList} />
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="employmentData.municipality" label="Municipality" items={municipalityOptions} showDescription descriptionText={municipalityText} />
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="employmentData.province" label="Province" items={provinceList} showDescription descriptionText={provinceText} />
                        </Box>
                    </Grid>
                </Grid>
                <Grid item xs={12} container spacing={3} justifyContent={'end'}>
                    <Grid item xs={6} sx={{ flex: 'revert !important' }}>
                        <Box mt={2}>
                            <LoadingButton loading={isSubmitting} variant='contained' form='personForm' type="submit" color='primary' sx={{ mr: 1 }}>
                                Save
                            </LoadingButton>
                        </Box>
                    </Grid>

                </Grid>

            </Grid>
        </>
    )
}

